import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Spinner,
  FlexView,
  NotFound,
  SelectDropdown,
} from '@gsa/afp-component-library';
import { useQuery } from '@apollo/client';
import { emDashUnicode } from '../../../utilities/constants';
import LineHistoryActions from './line-history-actions';
import { LineHistoryBreadcrumbs } from './line-history-breadcrumbs';
import LineHeader from '../line-template/line-header';
import TableWrapper from '../components/table-wrapper';
import { getColumns, getLineHistorySubrow } from './line-history-table-defs';
import LineHistoryFilters, { DEFAULT_FILTERS } from './line-history-filters';
import {
  GET_CONTRACT_LINE_HISTORY,
  GET_CONTRACT_LINE_BY_ID,
} from './line-history.gql';
import {
  filterAndSortVersions,
  processLineVersionData,
} from './line-history-helpers';
import './line-history.scss';
import { isContractSOP } from '../components/contract-helpers';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ITEMS_PER_PAGE = ITEMS_PER_PAGE_OPTIONS[0];

const ContractLineHistory = () => {
  const navigate = useNavigate();
  const { contractHeaderId, contractLineId } = useParams();

  const [paginationState, setPaginationState] = useState({
    limit: DEFAULT_ITEMS_PER_PAGE,
    offset: 0,
    currentPage: 1,
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [order, setOrder] = useState(['contractLineVersion', 'DESC']);

  const [lineVersions, setLineVersions] = useState([]);
  const [filteredVersions, setFilteredVersions] = useState([]);
  const [displayVersions, setDisplayVersions] = useState([]);

  const { data: lineData, loading: loadingLine } = useQuery(
    GET_CONTRACT_LINE_BY_ID,
    {
      variables: { contractLineId: +contractLineId },
      fetchPolicy: 'network-only',
      skip: !contractLineId,
    },
  );

  const {
    data: versionsData,
    loading: loadingHistory,
    called,
  } = useQuery(GET_CONTRACT_LINE_HISTORY, {
    variables: { contractLineId: +contractLineId },
    fetchPolicy: 'network-only',
    skip: !contractLineId,
    onCompleted: ({ getContractLineHistory }) => {
      const versions = processLineVersionData(getContractLineHistory);
      setLineVersions(versions);
      setFilteredVersions(versions);
      setDisplayVersions(versions.slice(0, DEFAULT_ITEMS_PER_PAGE));
    },
  });

  const contractLine = lineData?.getContractLineById;
  const solicitaion = contractLine?.contractHeader?.solicitation;
  const purchaseTypeCode = solicitaion?.purchaseTypeCode;
  const isSOP = isContractSOP(purchaseTypeCode);
  const isNonStandard = purchaseTypeCode === 'N';
  const isMas = purchaseTypeCode === 'M';
  const showManufacturerNumber =
    (isNonStandard && contractLine?.manufacturerModelNumber) || isMas;

  const maxContractLineHistoryVersion =
    versionsData?.getContractLineHistory?.reduce((prev, current) => {
      return prev.versionNumber > current.versionNumber ? prev : current;
    }, {}) || {};
  const maxContractLineVersionNumber =
    maxContractLineHistoryVersion.versionNumber;
  const maxContractHeaderHistoryVersionNumber =
    maxContractLineHistoryVersion.contractHeaderVersion?.versionNumber;

  const getURL = (headerId, lineId) =>
    `/catalog/contract/${headerId}/contract-line-template/${lineId}/history`;
  const selectedYear = getURL(contractHeaderId, contractLineId);
  const yearOptions = (contractLine?.contractLineRenewals ?? [])
    .map((clr) => {
      const { contractYear, renewalYear } = clr.contractHeader;
      return {
        value: getURL(clr.contractHeaderId, clr.id),
        label: `Contract year ${renewalYear ?? contractYear}`,
      };
    })
    .sort((a, b) => (a.label > b.label ? -1 : 1)); // desc by year

  const onPageChange = (newPage, itemsPerPage) => {
    const offset = (newPage - 1) * itemsPerPage;
    setPaginationState((prev) => ({
      ...prev,
      currentPage: newPage,
      offset,
      limit: itemsPerPage,
    }));
    setDisplayVersions(filteredVersions.slice(offset, offset + itemsPerPage));
  };

  const onSort = (orderBy) => {
    const [sortColumn, sortDirection] =
      typeof orderBy === 'string' ? orderBy?.split(' ') : [null, null];
    const newOrder = [sortColumn?.split('`')[1], sortDirection?.toUpperCase()];
    setOrder(newOrder);
    const filtered = filterAndSortVersions(filteredVersions, {}, newOrder);
    setFilteredVersions(filtered);
    const { offset, limit } = paginationState;
    setDisplayVersions(filtered.slice(offset, offset + limit));
  };

  const onFilterChange = (field, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [field]: value };
      const filtered = filterAndSortVersions(lineVersions, newFilters, order);
      setFilteredVersions(filtered);
      let { offset } = paginationState;
      if (filtered.length <= offset) {
        offset = 0;
        setPaginationState((prev) => ({ ...prev, offset, currentPage: 1 }));
      }
      setDisplayVersions(
        filtered.slice(offset, offset + paginationState.limit),
      );
      return newFilters;
    });
  };

  const resetFilters = () => {
    const filtered = filterAndSortVersions(
      lineVersions,
      DEFAULT_FILTERS,
      order,
    );
    setFilteredVersions(filtered);
    const { offset, limit } = paginationState;
    setDisplayVersions(filtered.slice(offset, offset + limit));
    setFilters(DEFAULT_FILTERS);
  };

  if (loadingLine || loadingHistory) {
    return (
      <div id="line-history">
        <LineHistoryBreadcrumbs scheduleLine="" />
        <Spinner />
      </div>
    );
  }

  if (called && !contractLine) {
    return (
      <div id="line-history">
        <LineHistoryBreadcrumbs scheduleLine="" />
        <NotFound />
      </div>
    );
  }

  if (called && (!versionsData || !lineData)) {
    return (
      <div id="line-history">
        <LineHistoryBreadcrumbs scheduleLine={contractLine?.scheduleLine} />
        <NotFound />
      </div>
    );
  }

  return (
    <div id="line-history">
      <LineHistoryBreadcrumbs scheduleLine={contractLine?.scheduleLine} />

      <FlexView vAlignContent="bottom" grow className="margin-y-2">
        <div className="title-xl">Line Item {contractLine?.scheduleLine}</div>
        <FlexView grow> </FlexView>
        <div className="margin-bottom-1">
          <SelectDropdown
            id="contract_year_sel"
            aria-label="contract year"
            onChange={({ target: { value } }) => navigate(value)}
            options={yearOptions}
            value={selectedYear}
            className="text-bold font-body-md"
            style={{ width: '13rem' }}
          />
        </div>
      </FlexView>

      <FlexView vAlignContent="center" grow className="margin-bottom-4">
        <FlexView>
          {showManufacturerNumber ? (
            <div className="title-m">
              <span className=" title-m-bold">Manufacturer model number: </span>
              {contractLine?.manufacturerModelNumber || emDashUnicode}
            </div>
          ) : (
            <>
              <div className="margin-right-2 title-m">
                <span className=" title-m-bold">Body: </span>
                {contractLine?.make?.makeName || emDashUnicode}{' '}
                {contractLine?.model?.modelName || emDashUnicode}
              </div>
              {contractLine?.chassisMake && (
                <div className="title-m">
                  <span className=" title-m-bold">Chassis: </span>
                  {contractLine?.chassisMake?.makeName || emDashUnicode}{' '}
                  {contractLine?.chassisModel?.modelName || emDashUnicode}
                </div>
              )}
            </>
          )}
        </FlexView>
        <FlexView grow> </FlexView>
        <LineHistoryActions />
      </FlexView>

      <LineHeader contractLine={contractLine} />

      <div className="title-m-bold margin-top-4 margin-bottom-2">
        Contract line history
      </div>

      <LineHistoryFilters
        allVersions={lineVersions}
        filters={filters}
        onFilterChange={onFilterChange}
        resetFilters={resetFilters}
      />

      <TableWrapper
        tableProps={{
          data: displayVersions,
          columns: getColumns(
            contractHeaderId,
            contractLineId,
            maxContractHeaderHistoryVersionNumber,
            maxContractLineVersionNumber,
          ),
          defaultSort: 'contractLineVersion DESC',
          expandable: true,
          order,
          onSort,
          renderRowSubComponent: getLineHistorySubrow(isSOP),
        }}
        paginationProps={{
          itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
          onPageChange,
          variant: 'advanced',
          itemsPerPage: paginationState.limit,
          currentPage: paginationState.currentPage,
          itemsCount: filteredVersions.length ?? 0,
        }}
        emptyStateText="No contract line history results found."
      />
    </div>
  );
};

export default ContractLineHistory;
